import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Virtuoso } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { VirtuosoTouchPanScroller } from '../../components/virtualized/VirtuosoTouchPanScroller';
import { ArrowLeft, ChatFill, LikeFill, Xmark } from '@peacecover/icon';
import { RoundBadge } from '@peacecover/component';
import HorizontalScroll from '../my-page/components/HorizontalScroll';
import { useSceneScrollManager } from './hooks/useSceneScrollManager';
import { getContentKey, useScenePaginationList } from './hooks/useScenePaginationList';
import { useOpenNextPreview } from './hooks/useOpenNextPreview';
import { useSceneDetailDisplay } from './hooks/useSceneDetailDisplay';
import {
  SCENE_READY_POLL_INTERVAL_MS,
  useGetSceneDetail
} from '../../domains/scene/ui/hooks/useGetSceneDetail';
import { useGetAdaptedFictionPreviewDetail } from '../../domains/scene/ui/hooks/useGetAdaptedFictionPreviewDetail';
import { useLikeAdaptedFictionEdition } from '../../domains/scene/ui/hooks/useLikeAdaptedFictionEdition';
import { useRegenerateScene } from '../../domains/scene/ui/hooks/useRegenerateScene';
import { useGetBettieBalances } from '../../domains/bettie/ui/hooks/useGetBettieBalances';
import { usePaymentSuccessMessage } from '../hooks/usePaymentSuccessMessage';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { getCastRelationshipId } from './utils/getCastRelationshipId';
import { getChatDirection } from './utils/getChatDirection';
import { getSceneTags } from './utils/getSceneTags';
import { getThreadSpeakerColor } from './utils/getThreadSpeakerColor';
import ThreadContentItem from './components/ThreadContentItem';
import ChatContentItem from './components/ChatContentItem';
import SceneContentItem from './components/SceneContentItem';
import CommentBottomSheet from './components/CommentBottomSheet';
import SceneOriginalProductList from './components/SceneOriginalProductList';
import BettieChargeBottomSheet from '../../components/bottomSheet/bettie/BettieChargeBottomSheet';
import OpenNextPreviewCard from './components/OpenNextPreviewCard';
import PreviewGeneratingPopup from './components/PreviewGeneratingPopup';
import OriginalEpisodesLoadingOverlay from './components/OriginalEpisodesLoadingOverlay';
import { SCENE_QUERY_KEYS } from '../../domains/scene/ui/queryKeys';
import { ROUTES } from '../../constants/routes';
import ProfileAvatar from '../my-page/components/ProfileAvatar';
import type { components } from '../../types/schema';

type CastRelationship = components['schemas']['ApiV1CastRelationship'];
type SceneContent =
  | components['schemas']['ApiV1SceneChat']
  | components['schemas']['ApiV1SceneThread'];
type ChatIntroContent = components['schemas']['ApiV1AdaptedFictionChatContent'];
type ThreadIntroContent = components['schemas']['ApiV1AdaptedFictionThreadContent'];
interface StatusBarDetail {
  likesCount: number;
  commentsCount: number;
  isLiked: boolean;
}

const HEART_COUNT_PER_PREVIEW = 1;

const ScenePreviewDetailPage = () => {
  const { sceneId } = useParams();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const adaptedFictionPreviewId = searchParams.get('adaptedFictionPreviewId');
  const isPreviewMode = !!adaptedFictionPreviewId;
  const [isCommentSheetOpen, setIsCommentSheetOpen] = useState(false);
  const [isBettieSheetOpen, setIsBettieSheetOpen] = useState(false);
  const [isGeneratingPopupOpen, setIsGeneratingPopupOpen] = useState(false);
  const [isEpisodesLoadingOverlayOpen, setIsEpisodesLoadingOverlayOpen] = useState(false);

  const [refetchInterval, setRefetchInterval] = useState<number | false>(false);
  const { data: sceneDetail, refetch: refetchSceneDetail } = useGetSceneDetail(sceneId ?? '', {
    refetchInterval
  });

  const isPendingMode =
    !isPreviewMode &&
    (sceneDetail?.status === 'pending' || sceneDetail?.status === 'generating') &&
    !!sceneDetail?.adaptedFictionPreviewId;
  const effectivePreviewMode = isPreviewMode || isPendingMode;
  const effectiveAdaptedFictionPreviewId =
    adaptedFictionPreviewId ?? (isPendingMode ? (sceneDetail?.adaptedFictionPreviewId ?? '') : '');

  const { data: bettieBalances } = useGetBettieBalances();
  const hasEnoughHearts =
    bettieBalances?.bettiePoint?.balance &&
    bettieBalances.bettiePoint.balance >= HEART_COUNT_PER_PREVIEW;

  useEffect(() => {
    if (sceneDetail) {
      if (sceneDetail.status === 'completed' || sceneDetail.status === 'preview_ready') {
        setRefetchInterval(false);
      } else {
        setRefetchInterval(SCENE_READY_POLL_INTERVAL_MS);
      }
    }
  }, [sceneDetail]);

  const { mutateAsync: likeAdaptedFictionEdition } = useLikeAdaptedFictionEdition();
  const { mutate: regenerateScene } = useRegenerateScene();
  const castRelationshipId = getCastRelationshipId();
  const hasRegeneratedRef = useRef(false);

  useEffect(() => {
    if (!hasRegeneratedRef.current && sceneDetail?.status === 'pending' && sceneId) {
      hasRegeneratedRef.current = true;
      regenerateScene(sceneId);
    }
  }, [sceneDetail?.status, sceneId, regenerateScene]);

  useEffect(() => {
    if (
      isPreviewMode &&
      adaptedFictionPreviewId &&
      castRelationshipId &&
      (sceneDetail?.status === 'preview_ready' || sceneDetail?.status === 'completed')
    ) {
      queryClient.invalidateQueries(
        SCENE_QUERY_KEYS.adaptedFictionPreviewDetail(adaptedFictionPreviewId, castRelationshipId)
      );
    }
  }, [
    isPreviewMode,
    adaptedFictionPreviewId,
    castRelationshipId,
    sceneDetail?.status,
    queryClient
  ]);
  const { data: previewDetail } = useGetAdaptedFictionPreviewDetail({
    adaptedFictionPreviewId: effectiveAdaptedFictionPreviewId,
    castRelationshipId,
    enabled: effectivePreviewMode && !!castRelationshipId
  });
  const effectiveScenePreviewId = effectivePreviewMode
    ? effectiveAdaptedFictionPreviewId
    : (sceneDetail?.adaptedFictionPreviewId ?? '');

  const rawContents = previewDetail?.introContents ?? [];
  const introContents =
    previewDetail?.previewType === 'SceneThread'
      ? rawContents.slice(0, 3)
      : rawContents.filter(
          (c): c is ChatIntroContent => 'sequenceGroupNo' in c && c.sequenceGroupNo <= 3
        );

  const paginationOptions = {
    fallbackCursor: effectivePreviewMode ? (previewDetail?.lastContentId ?? null) : null,
    disableBeforeMode: effectivePreviewMode,
    deferToClickOnly: effectivePreviewMode,
    contentsCount: effectivePreviewMode
      ? (previewDetail?.contentsCount ?? null)
      : (sceneDetail?.contentsCount ?? null),
    introContentsLength: effectivePreviewMode ? introContents.length : 0,
    maxGroupSequenceNoFromIntro:
      effectivePreviewMode && introContents.length > 0
        ? previewDetail?.previewType === 'SceneChat'
          ? Math.max(
              ...introContents.map((c) =>
                'sequenceGroupNo' in c ? (c as ChatIntroContent).sequenceGroupNo : 0
              )
            )
          : Math.max(
              ...introContents.map((c) =>
                'sequenceNo' in c ? (c as ThreadIntroContent).sequenceNo : 0
              )
            )
        : 0
  };

  const {
    contents,
    isBeforeMode,
    isLoading,
    isFetchingPreviousPage,
    isFetchingNextOne,
    forwardCursor,
    hasNoMoreContent,
    loadMoreBefore,
    handleOpenNextPreview,
    baseContentsLength,
    remainingPreviewsCount,
    totalCount,
    resetKey
  } = useScenePaginationList({
    sceneId,
    scenePreviewId: effectiveScenePreviewId,
    options: paginationOptions
  });

  const { display } = useSceneDetailDisplay({
    previewDetail,
    sceneDetail,
    contents,
    introContents,
    isPreviewMode: effectivePreviewMode,
    isLoading,
    isBeforeMode
  });

  const hasCursorForNext =
    forwardCursor != null || (effectivePreviewMode && !!previewDetail?.lastContentId);
  const canOpenNextPreview = !hasNoMoreContent && hasCursorForNext;
  const isGenerating =
    sceneDetail?.status !== 'completed' && sceneDetail?.status !== 'preview_ready';
  const shouldDisableOpenNext = (isGenerating ? false : !canOpenNextPreview) || isFetchingNextOne;

  const {
    showStarForIndicesFrom,
    isOpenNextPreviewCardLoading,
    handleOpenNextPreviewWithHeartCheck
  } = useOpenNextPreview({
    handleOpenNextPreview,
    isGenerating,
    hasEnoughHearts: !!hasEnoughHearts,
    contentsLength: contents.length,
    onNeedGeneratingPopup: () => setIsGeneratingPopupOpen(true),
    onNeedBettieSheet: () => setIsBettieSheetOpen(true)
  });

  const { virtuosoRef, hasScrolledRef, markPrependFromBaseLength } = useSceneScrollManager({
    isBeforeMode,
    baseContentsLength,
    isFetchingPreviousPage,
    contentsLength: contents.length
  });

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isFullAccessPurchased = sceneDetail?.fullAccessPurchased ?? false;

  const handleScrollToProductList = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    } else if (virtuosoRef.current) {
      virtuosoRef.current.scrollTo({
        top: Number.MAX_SAFE_INTEGER,
        behavior: 'smooth'
      });
    }
  };

  const handleProductButtonClick = () => {
    if (isFullAccessPurchased) {
      if (sceneDetail?.status === 'completed') {
        const baseParams = new URLSearchParams();
        if (sceneDetail.locator?.sceneEpisodeId)
          baseParams.set('episodeId', sceneDetail.locator.sceneEpisodeId);
        if (sceneDetail.locator?.paragraphIndex != null)
          baseParams.set('cursor', String(sceneDetail.locator.paragraphIndex));
        if (sceneDetail.locator?.charOffset != null)
          baseParams.set('charOffset', String(sceneDetail.locator.charOffset));
        const queryString = baseParams.toString();
        navigate(
          `${ROUTES.SCENE.ORIGINAL_EPISODES.GO(sceneId ?? '')}${queryString ? `?${queryString}` : ''}`
        );
      } else {
        setIsEpisodesLoadingOverlayOpen(true);
      }
    } else {
      handleScrollToProductList();
    }
  };

  useEffect(() => {
    void resetKey;
    hasScrolledRef.current = false;
  }, [resetKey, hasScrolledRef]);

  usePaymentSuccessMessage({
    onPaymentSuccess: () => {
      queryClient.invalidateQueries(SCENE_QUERY_KEYS.sceneDetail(sceneId));
    }
  });

  useEffect(() => {
    return () => {
      queryClient.invalidateQueries(SCENE_QUERY_KEYS.sceneSubscriptions());
    };
  }, [queryClient]);

  const handleLoadMoreBeforeWithScroll = () => {
    if (!isBeforeMode) return;
    markPrependFromBaseLength();
    hasScrolledRef.current = true;
    loadMoreBefore();
  };

  const getLastContentId = (): string | null => {
    if (contents.length === 0) return null;
    const last = contents[contents.length - 1];
    if ('sceneThreadId' in last && last.sceneThreadId) return last.sceneThreadId;
    if ('sceneChatId' in last && (last as { sceneChatId?: string }).sceneChatId) {
      return (last as { sceneChatId: string }).sceneChatId;
    }
    return null;
  };

  const handleLike = async () => {
    const adaptedFictionEditionId = effectivePreviewMode
      ? previewDetail?.adaptedFictionEditionId
      : sceneDetail?.adaptedFictionEditionId;
    if (!adaptedFictionEditionId) return;
    const lastContentId = effectivePreviewMode
      ? (getLastContentId() ?? previewDetail?.lastContentId ?? null)
      : getLastContentId();
    if (!lastContentId) return;
    try {
      const likeContentType =
        effectivePreviewMode &&
        (previewDetail?.previewType === 'SceneThread' || previewDetail?.previewType === 'SceneChat')
          ? previewDetail.previewType
          : !effectivePreviewMode &&
              (sceneDetail?.contentType === 'SceneThread' ||
                sceneDetail?.contentType === 'SceneChat')
            ? sceneDetail.contentType
            : contents.length > 0 && 'sceneThreadId' in contents[contents.length - 1]
              ? ('SceneThread' as const)
              : ('SceneChat' as const);

      const contexts = [
        { context_type: likeContentType, context_id: lastContentId },
        { context_type: 'CastRelationship' as const, context_id: castRelationshipId }
      ];
      await likeAdaptedFictionEdition({
        adaptedFictionEditionId,
        body: { engagement_contexts: contexts }
      });
      await refetchSceneDetail();
      if (effectivePreviewMode && effectiveAdaptedFictionPreviewId && castRelationshipId) {
        queryClient.invalidateQueries(
          SCENE_QUERY_KEYS.adaptedFictionPreviewDetail(
            effectiveAdaptedFictionPreviewId,
            castRelationshipId
          )
        );
      }
    } catch {
      // 좋아요 실패 시 무시
    }
  };

  return (
    <div className="flex min-h-screen flex-col overflow-hidden overscroll-none pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bg-background-dark">
      <Helmet>
        <title>{display.title}</title>
      </Helmet>
      <Header isPurchased={isFullAccessPurchased} onButtonClick={handleProductButtonClick} />
      <main className="relative flex flex-1 flex-col min-h-0 bg-[#000] mt-[43px]">
        {effectivePreviewMode || display.showIntroContents ? (
          <div
            ref={scrollContainerRef}
            className="absolute inset-0 min-h-0 min-w-0 overflow-x-hidden overflow-y-auto touch-pan-y hiddenScrollBar overscroll-none pb-[env(safe-area-inset-bottom)]"
          >
            <HeaderItem
              tags={getSceneTags(sceneDetail ?? null)}
              title={previewDetail?.fictionTitle ?? sceneDetail?.title ?? ''}
              author={previewDetail?.author ?? sceneDetail?.author}
              sequenceNo={previewDetail?.sequenceNo ?? sceneDetail?.sequenceNo ?? 0}
              totalCount={previewDetail?.totalCount ?? sceneDetail?.totalCount ?? 0}
              previewType={
                previewDetail?.previewType ??
                (sceneDetail?.contentType as 'SceneChat' | 'SceneThread' | undefined)
              }
            />
            {previewDetail?.previewType === 'SceneThread'
              ? (introContents as ThreadIntroContent[]).map((content, index) => (
                  <ThreadContentItem
                    key={`intro-${content.text}-${index}`}
                    speaker={content.speaker ?? undefined}
                    threadType={content.threadType}
                    text={content.text}
                    backgroundColor={getThreadSpeakerColor(
                      display.speakerOrderMap.get(content.speaker ?? '') ?? 0
                    )}
                    hasLine={
                      index < introContents.length - 1 ||
                      contents.length > 0 ||
                      (isPreviewMode && !hasNoMoreContent && remainingPreviewsCount !== 0)
                    }
                    className={content.threadType === 'passage' ? '!pl-[24px] !pr-[16px]' : ''}
                  />
                ))
              : (introContents as ChatIntroContent[]).map((content, index, arr) => (
                  <ChatContentItem
                    key={`intro-${content.text}-${index}`}
                    chatType={content.chatType}
                    speaker={content.speaker ?? ''}
                    text={content.text}
                    pictureUrl={content.pictureUrl}
                    direction={getChatDirection(display.detailForHeader, content.speaker ?? '')}
                    className={content.chatType === 'passage' ? '!pl-[24px] !pr-[16px]' : ''}
                    showStar={false}
                    showSpeaker={
                      index === 0 ||
                      !['dialogue', 'thought'].includes(arr[index - 1].chatType ?? '') ||
                      arr[index - 1].speaker !== content.speaker
                    }
                  />
                ))}
            {contents.map((content, index) => {
              const prev = index > 0 ? contents[index - 1] : null;
              const showSpeaker =
                !prev ||
                !('chatType' in prev) ||
                !['dialogue', 'thought'].includes(prev.chatType ?? '') ||
                prev.speaker !== (content as components['schemas']['ApiV1SceneChat']).speaker;
              return (
                <SceneContentItem
                  key={getContentKey(content)}
                  content={content}
                  displayContentType={display.contentType}
                  index={index}
                  contentsLength={contents.length}
                  totalCount={totalCount}
                  showStarForIndicesFrom={showStarForIndicesFrom}
                  speakerOrderMap={display.speakerOrderMap}
                  detailForHeader={display.detailForHeader}
                  showSpeaker={showSpeaker}
                />
              );
            })}
            {!hasNoMoreContent && remainingPreviewsCount !== 0 && (
              <OpenNextPreviewCard
                remainingPreviewsCount={remainingPreviewsCount}
                speaker={display.lastSpeaker}
                backgroundColor={getThreadSpeakerColor(
                  display.speakerOrderMap.get(display.lastSpeaker ?? '') ?? 0
                )}
                disabled={shouldDisableOpenNext}
                onClick={handleOpenNextPreviewWithHeartCheck}
                isThread={display.contentType === 'SceneThread'}
                isLoading={isOpenNextPreviewCardLoading}
                isGenerating={isGenerating}
              />
            )}
            <SceneOriginalProductList
              sceneId={sceneId ?? ''}
              sceneDetail={sceneDetail ?? null}
              castRelationshipId={castRelationshipId}
              isFullAccessPurchased={sceneDetail?.fullAccessPurchased ?? false}
              castRelationship={sceneDetail?.castRelationship ?? null}
            />
          </div>
        ) : isLoading || contents.length === 0 ? null : (
          <div className="absolute inset-0 min-h-0 min-w-0 overflow-x-hidden touch-pan-y pb-[env(safe-area-inset-bottom)]">
            <Virtuoso
              ref={virtuosoRef}
              overscan={300}
              defaultItemHeight={80}
              style={{ height: '100%' }}
              data={contents}
              startReached={isBeforeMode ? handleLoadMoreBeforeWithScroll : undefined}
              atTopStateChange={
                isBeforeMode ? (atTop) => atTop && handleLoadMoreBeforeWithScroll() : undefined
              }
              atBottomThreshold={isBeforeMode ? undefined : 200}
              computeItemKey={(_index, content: SceneContent) => getContentKey(content)}
              itemContent={(index, content: SceneContent) => {
                const prev = index > 0 ? contents[index - 1] : null;
                const showSpeaker =
                  !prev ||
                  !('chatType' in prev) ||
                  !['dialogue', 'thought'].includes(prev.chatType ?? '') ||
                  prev.speaker !== (content as components['schemas']['ApiV1SceneChat']).speaker;
                return (
                  <SceneContentItem
                    content={content}
                    displayContentType={display.contentType}
                    index={index}
                    contentsLength={contents.length}
                    totalCount={totalCount}
                    showStarForIndicesFrom={showStarForIndicesFrom}
                    speakerOrderMap={display.speakerOrderMap}
                    detailForHeader={display.detailForHeader}
                    showSpeaker={showSpeaker}
                  />
                );
              }}
              components={{
                Scroller: VirtuosoTouchPanScroller,
                Header: () => (
                  <>
                    {isBeforeMode && isFetchingPreviousPage ? (
                      <div className="px-[22px] pt-[12px]">
                        <BeforeTopLoadingSkeleton />
                      </div>
                    ) : null}
                    {effectivePreviewMode || display.hasSequenceOneInList ? (
                      <HeaderItem
                        tags={getSceneTags(sceneDetail ?? null)}
                        title={previewDetail?.fictionTitle ?? sceneDetail?.title ?? ''}
                        author={previewDetail?.author ?? sceneDetail?.author}
                        sequenceNo={previewDetail?.sequenceNo ?? sceneDetail?.sequenceNo ?? 0}
                        totalCount={previewDetail?.totalCount ?? sceneDetail?.totalCount ?? 0}
                        previewType={
                          previewDetail?.previewType ??
                          (sceneDetail?.contentType as 'SceneChat' | 'SceneThread' | undefined)
                        }
                      />
                    ) : null}
                  </>
                ),
                Footer: () => (
                  <>
                    {!hasNoMoreContent && remainingPreviewsCount !== 0 && (
                      <OpenNextPreviewCard
                        remainingPreviewsCount={remainingPreviewsCount}
                        speaker={display.lastSpeaker}
                        backgroundColor={getThreadSpeakerColor(
                          display.speakerOrderMap.get(display.lastSpeaker ?? '') ?? 0
                        )}
                        disabled={shouldDisableOpenNext}
                        onClick={handleOpenNextPreviewWithHeartCheck}
                        isThread={display.contentType === 'SceneThread'}
                        isLoading={isOpenNextPreviewCardLoading}
                        isGenerating={isGenerating}
                      />
                    )}
                    <SceneOriginalProductList
                      sceneId={sceneId ?? ''}
                      sceneDetail={sceneDetail ?? null}
                      castRelationshipId={castRelationshipId}
                      isFullAccessPurchased={sceneDetail?.fullAccessPurchased ?? false}
                      castRelationship={sceneDetail?.castRelationship ?? null}
                    />
                  </>
                )
              }}
            />
          </div>
        )}
      </main>
      <SceneStatusBar
        detail={display.statusBarDetail}
        castRelationship={display.detailForHeader}
        handleLike={handleLike}
        handleComment={() => setIsCommentSheetOpen(true)}
      />
      {display.effectiveAdaptedFictionEditionId && sceneId && (
        <CommentBottomSheet
          adaptedFictionEditionId={display.effectiveAdaptedFictionEditionId}
          sceneId={sceneId}
          isOpen={isCommentSheetOpen}
          onClose={() => setIsCommentSheetOpen(false)}
          snapToBottom
          isDarkMode={true}
        />
      )}
      <BettieChargeBottomSheet
        isOpen={isBettieSheetOpen}
        onClose={() => setIsBettieSheetOpen(false)}
        onetimeOnly
      />
      {isGeneratingPopupOpen && (
        <PreviewGeneratingPopup
          castRelationship={display.detailForHeader}
          onClose={() => setIsGeneratingPopupOpen(false)}
        />
      )}
      {isEpisodesLoadingOverlayOpen && (
        <OriginalEpisodesLoadingOverlay onClose={() => setIsEpisodesLoadingOverlayOpen(false)} />
      )}
    </div>
  );
};

export default ScenePreviewDetailPage;

const Header = ({
  isPurchased,
  onButtonClick
}: {
  isPurchased: boolean;
  onButtonClick: () => void;
}) => {
  const { t } = useTranslation();
  const handleHistoryBack = useHandleHistoryBack();
  return (
    <header className="flex flex-col bg-black fixed top-0 left-0 right-0 z-10 pt-[env(safe-area-inset-top)]">
      <div className="flex items-center justify-between w-full pl-[16px] py-[7px] pr-[14px] relative">
        <button type="button" onClick={handleHistoryBack}>
          <ArrowLeft size={24} className="text-main-dark-1" />
        </button>
        <button
          type="button"
          onClick={onButtonClick}
          className={`p-[1px] rounded-full ${isPurchased ? 'bg-white' : 'bg-[linear-gradient(47deg,#45DCAE_0%,#5D3EFA_99.08%)]'}`}
        >
          <span className="flex items-center bg-black rounded-full px-[11px] py-[7px]">
            <span
              className={`text-[13px] font-semibold leading-none ${isPurchased ? 'text-white' : 'bg-[linear-gradient(47deg,#45DCAE_0%,#5D3EFA_99.08%)] bg-clip-text text-transparent'}`}
            >
              {isPurchased
                ? t('ScenePreviewDetail.readNovel')
                : t('ScenePreviewDetail.purchaseNovel')}
            </span>
          </span>
        </button>
      </div>
    </header>
  );
};

const SceneStatusBar = ({
  detail,
  castRelationship,
  handleLike,
  handleComment
}: {
  detail: StatusBarDetail | null;
  castRelationship: CastRelationship | null;
  handleLike: () => void;
  handleComment: () => void;
}) => {
  const navigate = useNavigate();
  return (
    <div className="fixed bottom-0 left-0 right-0 z-10 pb-[calc(env(safe-area-inset-bottom)+10px)] bg-pure-black pt-[16px] rounded-t-[20px] px-[20px] flex items-center justify-between">
      <div className="flex items-center gap-[24px]">
        <button type="button" className="flex flex-col items-center" onClick={handleLike}>
          <LikeFill
            className="text-pure-white"
            gradientStops={
              detail?.isLiked
                ? [
                    { offset: '0%', color: '#FF45A9' },
                    { offset: '100%', color: '#FF6A6A' }
                  ]
                : []
            }
          />
          <p className="text-[11px] font-semibold leading-[13px] text-pure-white">
            {detail?.likesCount ?? 0}
          </p>
        </button>
        <button type="button" className="flex flex-col items-center" onClick={handleComment}>
          <ChatFill className="text-pure-white" />
          <p className="text-[11px] font-semibold leading-[13px] text-pure-white">
            {detail?.commentsCount ?? 0}
          </p>
        </button>
      </div>
      <button
        type="button"
        onClick={() => navigate(ROUTES.SCENE.CAST_RELATIONSHIPS)}
        className="px-[9px] py-[8px] bg-[rgba(255,255,255,0.05)] rounded-full flex items-center gap-[7px] flex-shrink-0"
      >
        <div className="flex items-center gap-[4px]">
          <ProfileAvatar
            thumbnailUrl={castRelationship?.personA.thumbnailUrl}
            widthAndHeight="27px"
            svgWidthAndHeight="27px"
          />
          <span className="text-[12px] font-medium leading-[14px] text-pure-white">
            {castRelationship?.personA.name}
          </span>
        </div>
        <Xmark size={12} className="text-main-dark-3" />
        <div className="flex items-center gap-[4px]">
          <ProfileAvatar
            thumbnailUrl={castRelationship?.personB.thumbnailUrl}
            widthAndHeight="27px"
            svgWidthAndHeight="27px"
          />
          <span className="text-[12px] font-medium leading-[14px] text-pure-white">
            {castRelationship?.personB.name}
          </span>
        </div>
      </button>
    </div>
  );
};

const BeforeTopLoadingSkeleton = () => {
  return (
    <output className="flex gap-[10px] items-stretch py-[15px] animate-pulse" aria-live="polite">
      <div className="flex flex-col items-center gap-[8px]">
        <div className="w-[26px] h-[26px] rounded-full bg-white/10 shrink-0" />
        <div className="w-[2px] flex-1 min-h-[40px] rounded-[16px] bg-white/10" />
      </div>
      <div className="flex flex-col gap-[5px] flex-1 min-w-0">
        <div className="h-[17px] w-[120px] rounded bg-white/10" />
        <div className="h-[19px] w-full rounded bg-white/10" />
        <div className="h-[19px] w-[92%] rounded bg-white/10" />
        <div className="h-[19px] w-[78%] rounded bg-white/10" />
      </div>
    </output>
  );
};

const HeaderItem = ({
  tags,
  title,
  author,
  sequenceNo,
  totalCount,
  previewType
}: {
  tags: string[];
  title: string;
  author?: string | null;
  sequenceNo: number;
  totalCount: number;
  previewType?: 'SceneChat' | 'SceneThread';
}) => {
  const { t } = useTranslation();
  const previewTypeLabel =
    previewType === 'SceneThread'
      ? t('ScenePreviewDetail.thread')
      : t('ScenePreviewDetail.conversation');
  return (
    <div className="mx-[21px] pt-[8px] pb-[10px] mb-[18px] border-b border-[#3B3B3B]">
      <div className="flex items-center gap-[10px] mb-[12px]">
        <div
          className="p-[1px] rounded-[6px] w-fit"
          style={{
            background:
              'linear-gradient(to bottom, rgba(255,255,255,0.8), rgba(255,255,255,0.3), rgba(255,255,255,0.8))'
          }}
        >
          <div className="rounded-[5px] bg-black px-[10px] py-[9px] flex flex-col gap-[3px] items-center text-white">
            <span className="text-[12px] font-semibold leading-[14px]">Preview</span>
            <span className="text-[10px] leading-[14px]">
              Part {sequenceNo}/{totalCount}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-start gap-[5px]">
          <div className="flex items-end gap-[6px]">
            <p className="text-[17px] font-semibold leading-[20px] text-white">{title}</p>
            {author && (
              <p className="text-[11px] leading-[13px] text-[rgba(255,255,255,0.80)]">
                {t('ScenePreviewDetail.author')} <span className="font-semibold">{author}</span>
              </p>
            )}
          </div>
          <p className="text-[13px] font-semibold leading-[16px] text-[#00D4FF]">
            {t('ScenePreviewDetail.previewTypePrompt', {
              type: previewTypeLabel
            })}
          </p>
        </div>
      </div>
      {tags.length > 0 && (
        <HorizontalScroll>
          <div className="flex items-center gap-[8px] py-[10px]">
            {tags.map((tag) => (
              <RoundBadge
                key={tag}
                className="bg-[rgba(255,255,255,0.20)] text-pure-white leading-[13px] shrink-0"
              >
                {tag}
              </RoundBadge>
            ))}
          </div>
        </HorizontalScroll>
      )}
    </div>
  );
};